// ── Preiskatalog API client ────────────────────────────────────────────────────
// Hooks for reading catalog articles + coverage, and upload/commit functions.
// Backend endpoints are in functions/intranet/src/functions/preiskatalog.js.

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import type { RechnungQuelle } from './lieferantenRechnungenApi'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CatalogCoverageItem {
  supplier_id: string
  article_count: number
  /** ISO date string (YYYY-MM-DD) of the latest catalog_date imported */
  latest_catalog_date: string | null
  /** ISO datetime of the latest committed import */
  latest_import_at: string | null
}

export interface CatalogCoverageResponse {
  suppliers: CatalogCoverageItem[]
}

/** No information → 'konventionell' with bio_quelle 'standard' ("kein Bio" by default). */
export type BioStatus = 'bio' | 'konventionell' | 'zertifiziert'
/** Bio-status filter; 'manuell' = set by hand, 'ohne_angabe' = default "kein Bio" (worklist). */
export type BioStatusFilter = BioStatus | 'manuell' | 'ohne_angabe'
/** Product class: Lebensmittel counts in the Bio-Quote; the other four are Zusatzprodukte. */
export type Warenart = 'lebensmittel' | 'non_food' | 'pfand' | 'gebuehr' | 'alkohol'
/** Warenart filter; 'zusatz' = everything except Lebensmittel, 'manuell' = set by hand. */
export type WarenartFilter = Warenart | 'zusatz' | 'manuell'
export type ProductSortKey = 'artikel_nr' | 'artikelname' | 'lieferant' | 'preis' | 'bio_status' | 'warenart' | 'last_seen' | 'total_net'

export interface CatalogArticleRow {
  supplier_id: string
  artikel_nr: string
  artikelname: string
  artikelname_alt: string | null
  /** Current open list price (net) from ops.CatalogPrice */
  list_price: number | null
  catalog_unit: string | null
  pack_qty: number | null
  base_unit: string | null
  vpe_raw: string | null
  vat_rate: number | null
  /** ISO date (YYYY-MM-DD) */
  valid_from: string | null
  /** ISO date (YYYY-MM-DD) or null = open */
  valid_to: string | null
  is_food: boolean
  is_organic: boolean
  is_halal: boolean
  is_vegetarian: boolean
  is_vegan: boolean
  category_1: string | null
  category_2: string | null
  category_3: string | null
  unit_parse_status: 'ok' | 'unparsed' | 'manual'
  extras: Record<string, unknown> | null
  /** Detail endpoint only: the supplier's article record exactly as delivered (CC JSON push). */
  raw?: Record<string, unknown> | null
  /** True when extras.price_missing === true — article imported with list_price 0. */
  price_missing?: boolean
  // ── Fields of the list endpoint only (the single-article endpoint omits them) ──
  /** 'rechnung' = no catalog row; price/unit come from the last invoice. */
  quelle?: 'katalog' | 'rechnung'
  /** Where list_price comes from: catalog, or the last invoice (catalog has none, e.g. Rosenkrantz "on_request"). */
  preis_quelle?: 'katalog' | 'rechnung'
  /** Price from invoices only: 'fee' = Gebühr (Preis je Lieferung), 'credit_only' = nur Gutschriften (kein Preis). */
  price_kind?: 'fee' | 'credit_only' | null
  /** Gebühren-Familie (erm. + volle MwSt): Anteil dieses Artikels an list_price (der ganzen Liefergebühr). */
  fee_part?: number | null
  bio_status?: BioStatus
  bio_quelle?: 'manuell' | 'lieferant' | 'name' | 'standard' | null
  bio_updated_by?: string | null
  /** ISO datetime (UTC) */
  bio_updated_at?: string | null
  warenart?: Warenart
  warenart_quelle?: 'manuell' | 'regel'
  warenart_updated_by?: string | null
  /** ISO datetime (UTC) */
  warenart_updated_at?: string | null
  /** YYYY-MM-DD of the last invoice */
  last_seen?: string | null
  /** Lifetime net € purchased */
  total_net?: number | null
}

/** Selection key of an article row. */
export const rowKey = (r: { supplier_id: string; artikel_nr: string }) => `${r.supplier_id}|${r.artikel_nr}`

export interface CatalogArticlesParams {
  supplier_id?: string
  q?: string
  category_1?: string
  bio_status?: BioStatusFilter
  warenart?: WarenartFilter
  quelle?: 'katalog' | 'rechnung'
  /** Only products that appeared on an invoice. */
  gekauft?: boolean
  sort?: ProductSortKey
  dir?: 'asc' | 'desc'
  /** Stichtag YYYY-MM-DD — price valid ON that date (SCD2 as-of); omit for today's open row. */
  as_of?: string
  page?: number
  pageSize?: number
}

export interface CatalogArticlesResponse {
  rows: CatalogArticleRow[]
  totalRows: number
  page: number
  pageSize: number
}

/** Preview returned after a successful catalog file upload (before commit). */
export interface CatalogUploadPreview {
  batchId: string
  supplier_id: string
  catalog_date: string | null
  article_count: number
  new_count: number
  changed_count: number
  unchanged_count: number
  klaerung_count: number
  /** Number of rows whose list price could not be parsed; imported with list_price 0
   *  and extras.price_missing = true. NOT included in klaerung_count. */
  price_missing_count: number
  /** Number of pack-unit (Gebinde) duplicates collapsed into a single article row.
   *  Informational only — article_count already reflects the collapsed total. */
  collapsed_count: number
  /** Only present for Böttjer two-file uploads where the same Art.nr appears in
   *  both files with different prices. Must be resolved before commit is allowed. */
  conflicts: Array<{
    artikel_nr: string
    file1_price: number
    file2_price: number
    file1_name: string
    file2_name: string
  }>
  klaerung: Array<{
    artikel_nr: string | null
    reason: string
    raw_row: string | null
  }>
  status: 'created' | 'previewed' | 'failed'
  error_detail: string | null
}

export interface CatalogCommitResponse {
  batchId: string
  status: 'committed'
  article_count: number
  new_count: number
  changed_count: number
  unchanged_count: number
  klaerung_count: number
  completed_at: string
}

// ─── Read hooks ───────────────────────────────────────────────────────────────

/** One supplier that has products in the Preiskatalog (catalog and/or invoices). */
export interface CatalogSupplier {
  supplier_id: string
  name: string
  /** Articles in the supplier's own price catalog */
  katalog: number
  /** Articles known only from invoices (no catalog row) */
  rechnung: number
}

/**
 * Suppliers with products in the Preiskatalog, sorted by name — derived from
 * the data, so non-goods entries in dim.Lieferanten (PCA, Re-Food) never show.
 */
export function useCatalogSuppliers() {
  return useQuery({
    queryKey: ['catalog-suppliers'] as const,
    queryFn: async (): Promise<CatalogSupplier[]> => {
      const res = await fetch('/api/preiskatalog/suppliers', { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Lieferanten konnten nicht geladen werden (${res.status})`)
      }
      return ((await res.json()) as { suppliers: CatalogSupplier[] }).suppliers
    },
    staleTime: 5 * 60_000,
  })
}

/**
 * Coverage summary — one item per supplier that has committed catalog data.
 * Used by SupplierCoverageBanner.
 */
export function useCatalogCoverage() {
  return useQuery({
    queryKey: ['catalog-coverage'] as const,
    queryFn: async (): Promise<CatalogCoverageResponse> => {
      const res = await fetch('/api/preiskatalog/coverage', { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Coverage konnte nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    staleTime: 60_000,
  })
}

/**
 * Paged catalog article list with optional filtering.
 */
export function useCatalogArticles(params: CatalogArticlesParams) {
  const qs = new URLSearchParams()
  if (params.supplier_id)        qs.set('supplier_id', params.supplier_id)
  if (params.q)                  qs.set('q', params.q)
  if (params.category_1)         qs.set('category_1', params.category_1)
  if (params.bio_status)         qs.set('bio_status', params.bio_status)
  if (params.warenart)           qs.set('warenart', params.warenart)
  if (params.quelle)             qs.set('quelle', params.quelle)
  if (params.gekauft)            qs.set('gekauft', '1')
  if (params.sort)               qs.set('sort', params.sort)
  if (params.sort && params.dir) qs.set('dir', params.dir)
  if (params.as_of)              qs.set('as_of', params.as_of)
  if (params.page != null)       qs.set('page', String(params.page))
  if (params.pageSize != null)   qs.set('pageSize', String(params.pageSize))

  return useQuery({
    queryKey: ['catalog-articles', params] as const,
    queryFn: async (): Promise<CatalogArticlesResponse> => {
      const res = await fetch(`/api/preiskatalog/articles?${qs.toString()}`, { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Artikelliste konnte nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

// ─── Bio status (single / bulk) ───────────────────────────────────────────────

export interface BioStatusFilterBody {
  supplier_id?: string
  q?: string
  category_1?: string
  bio_status?: BioStatusFilter
  warenart?: WarenartFilter
  quelle?: 'katalog' | 'rechnung'
  gekauft?: '1'
  as_of?: string
}

/** Selected rows (max 5000) OR "all hits of the filter" (server refuses with 409 if its count differs). */
type ClassificationTarget =
  | { items: Array<{ supplier_id: string; artikel_nr: string }> }
  | { filter: BioStatusFilterBody; expected_count: number }
export type SetBioStatusBody = { status: BioStatus | null } & ClassificationTarget
export type SetWarenartBody = { status: Warenart | null } & ClassificationTarget

const CLASSIFICATION_ENDPOINT = {
  bio:      { url: '/api/preiskatalog/bio-status', error: 'Bio-Status konnte nicht gespeichert werden' },
  warenart: { url: '/api/preiskatalog/warenart',   error: 'Warenart konnte nicht gespeichert werden' },
} as const

/** POST /api/preiskatalog/bio-status | /warenart — status null = reset to automatic. */
export function useSetClassification(kind: 'bio' | 'warenart') {
  const qc = useQueryClient()
  const { url, error } = CLASSIFICATION_ENDPOINT[kind]
  return useMutation({
    mutationFn: async (body: SetBioStatusBody | SetWarenartBody): Promise<{ count: number; changed: number }> => {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const b = await res.json().catch(() => ({}))
        throw new Error((b as { error?: string }).error ?? `${error} (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog-articles'] })
      qc.invalidateQueries({ queryKey: ['catalog-article'] })
    },
  })
}

export const useSetBioStatus = () => useSetClassification('bio')
export const useSetWarenart = () => useSetClassification('warenart')

/**
 * Single article detail (all fields + latest price).
 */
export function useCatalogArticle(supplierId: string | null, artikelNr: string | null) {
  return useQuery({
    queryKey: ['catalog-article', supplierId, artikelNr] as const,
    queryFn: async (): Promise<CatalogArticleRow> => {
      const res = await fetch(
        `/api/preiskatalog/articles/${encodeURIComponent(supplierId!)}/${encodeURIComponent(artikelNr!)}`,
        { credentials: 'include' },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Artikel konnte nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled: Boolean(supplierId) && Boolean(artikelNr),
    staleTime: 60_000,
    retry: 1,
  })
}

// ─── Upload + commit (imperative, not hooks) ──────────────────────────────────

/**
 * Upload one or more catalog files (synchronous multipart POST).
 * - Böttjer: up to 2 .xls files → merged two-file batch.
 * - Brünning: 1 .xml file.
 * - ChefsCulinar: 1 .json file.
 *
 * Returns a CatalogUploadPreview immediately (no background queue).
 *
 * @param files  Array of File objects from the file input.
 * @param supplierId  'boettjer' | 'bruening' | 'chefsculinar'
 * @param validFrom  ISO date string 'YYYY-MM-DD' (required for Böttjer; ignored for others).
 */
export async function uploadKatalogFile(
  files: File[],
  supplierId: string,
  validFrom?: string,
): Promise<CatalogUploadPreview> {
  if (files.length === 0) throw new Error('Keine Datei ausgewählt.')

  const form = new FormData()
  for (const f of files) form.append('files', f)
  form.append('supplier_id', supplierId)
  if (validFrom) form.append('valid_from', validFrom)

  const res = await fetch('/api/imports/katalog/upload', {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw Object.assign(
      new Error((body as { error?: string }).error ?? `Upload fehlgeschlagen (${res.status})`),
      { code: (body as { code?: string }).code ?? null },
    )
  }
  return res.json()
}

/**
 * Commit a previewed catalog batch.
 * Only allowed when batch status = 'previewed' and conflicts = [].
 */
export async function commitKatalogBatch(batchId: string): Promise<CatalogCommitResponse> {
  const res = await fetch(`/api/imports/katalog/${encodeURIComponent(batchId)}/commit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `Commit fehlgeschlagen (${res.status})`)
  }
  return res.json()
}

// ─── Price deviation types + hooks ───────────────────────────────────────────

/**
 * One row from GET /api/preiskatalog/deviations.
 *
 * Suppliers covered: chefsculinar · boettjer · winfood (catalog-capable + invoice-data-ready).
 * delta_eur = invoice_price − catalog_price (per unit)
 * delta_pct = (invoice_price − catalog_price) / NULLIF(catalog_price, 0) * 100
 * Only rows where delta_pct >= @min_pct are returned (we paid MORE than list).
 */
export interface DeviationRow {
  supplier_id: string
  artikel_nr: string
  artikelname: string
  /** Current open catalog list price (net) from ops.CatalogPrice WHERE valid_to IS NULL */
  catalog_price: number
  /** Latest invoice unit price from fact.SupplierRechnung */
  invoice_price: number
  /** ISO date string of the most recent invoice for this artikel */
  invoice_date: string
  /** (invoice_price − catalog_price) / catalog_price * 100 */
  delta_pct: number
  /** invoice_price − catalog_price per unit */
  delta_eur: number
}

export interface DeviationsResponse {
  supplier_id: string
  count: number
  rows: DeviationRow[]
}

/**
 * Price history point from GET /api/preiskatalog/articles/:supplierId/:artikelNr/history.
 * One entry per ops.CatalogPrice row for that (supplier, artikel), ordered ASC by valid_from.
 */
export interface PriceHistoryPoint {
  /** ISO date string (YYYY-MM-DD) */
  valid_from: string
  /** ISO date string (YYYY-MM-DD) or null = currently open */
  valid_to: string | null
  list_price: number
}

export interface ArticleHistoryResponse {
  points: PriceHistoryPoint[]
}

// ─── Read hooks (deviations + article history) ────────────────────────────────

/**
 * Catalog price deviations for a single supplier.
 *
 * Hits GET /api/preiskatalog/deviations?supplier_id=&min_pct=
 * Only enabled when supplierId is set.
 *
 * @param supplierId  'chefsculinar' | 'boettjer' | 'winfood'
 * @param minPct      Minimum delta_pct threshold (default 0 — all overcharges).
 */
export function useDeviations(supplierId: string, minPct: number = 0) {
  const qs = new URLSearchParams({ supplier_id: supplierId, min_pct: String(minPct) })

  return useQuery({
    queryKey: ['catalog-deviations', supplierId, minPct] as const,
    queryFn: async (): Promise<DeviationsResponse> => {
      const res = await fetch(`/api/preiskatalog/deviations?${qs.toString()}`, { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Preisabweichungen konnten nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled: Boolean(supplierId),
    staleTime: 60_000,
  })
}

/**
 * Full catalog price history for a single article.
 *
 * Hits GET /api/preiskatalog/articles/:supplierId/:artikelNr/history
 * Only enabled when both supplierId and artikelNr are non-empty.
 */
export function useArticleHistory(supplierId: string | null, artikelNr: string | null) {
  return useQuery({
    queryKey: ['catalog-article-history', supplierId, artikelNr] as const,
    queryFn: async (): Promise<ArticleHistoryResponse> => {
      const res = await fetch(
        `/api/preiskatalog/articles/${encodeURIComponent(supplierId!)}/${encodeURIComponent(artikelNr!)}/history`,
        { credentials: 'include' },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Preishistorie konnte nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled: Boolean(supplierId) && Boolean(artikelNr),
    staleTime: 60_000,
  })
}

// ─── Client-side CSV export ───────────────────────────────────────────────────

/**
 * Build a semicolon-delimited, de-DE formatted CSV from the given article rows
 * and trigger a browser download.
 *
 * Format:
 *  - Encoding:    UTF-8 with BOM (Excel-safe)
 *  - Delimiter:   semicolon (;)
 *  - Decimal sep: comma (de-DE convention)
 *  - Line ending: CRLF
 *
 * No backend call — pure client-side Blob + anchor pattern.
 *
 * @param rows      CatalogArticleRow[] to export (already filtered/sorted by the caller).
 * @param filename  Download filename, e.g. 'preiskatalog-chefsculinar.csv'.
 */
export function exportArticlesCsv(rows: CatalogArticleRow[], filename: string): void {
  const BOM = '﻿'
  const SEP = ';'
  const CRLF = '\r\n'

  const formatNumber = (n: number | null | undefined): string => {
    if (n == null) return ''
    // de-DE: decimal comma, no thousands separator
    return n.toLocaleString('de-DE', { useGrouping: false, maximumFractionDigits: 4 })
  }

  const escape = (v: string | null | undefined): string => {
    const s = v ?? ''
    // Wrap in double-quotes if the value contains the delimiter, a quote, or a newline
    if (s.includes(SEP) || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }

  const headers: string[] = [
    'Lieferant',
    'Art.-Nr.',
    'Artikelname',
    'Artikelname (alt)',
    'Listenpreis (netto)',
    'Einheit (Katalog)',
    'VPE-Menge',
    'Basiseinheit',
    'VPE (original)',
    'MwSt. %',
    'Gültig ab',
    'Gültig bis',
    'Lebensmittel',
    'Bio',
    'Halal',
    'Vegetarisch',
    'Vegan',
    'Kategorie 1',
    'Kategorie 2',
    'Kategorie 3',
    'Einheit-Status',
    'Quelle',
    'Bio-Status',
    'Bio-Quelle',
    'Warenart',
    'Warenart-Quelle',
    'Zuletzt gekauft',
    'Einkauf netto €',
  ]

  const toRow = (r: CatalogArticleRow): string[] => [
    r.supplier_id,
    r.artikel_nr,
    r.artikelname,
    r.artikelname_alt ?? '',
    formatNumber(r.list_price),
    r.catalog_unit ?? '',
    formatNumber(r.pack_qty),
    r.base_unit ?? '',
    r.vpe_raw ?? '',
    formatNumber(r.vat_rate),
    r.valid_from ?? '',
    r.valid_to ?? '',
    r.is_food ? 'Ja' : 'Nein',
    r.is_organic ? 'Ja' : 'Nein',
    r.is_halal ? 'Ja' : 'Nein',
    r.is_vegetarian ? 'Ja' : 'Nein',
    r.is_vegan ? 'Ja' : 'Nein',
    r.category_1 ?? '',
    r.category_2 ?? '',
    r.category_3 ?? '',
    r.unit_parse_status,
    r.quelle === 'rechnung' ? 'Rechnung' : 'Katalog',
    r.bio_status ?? '',
    r.bio_quelle ?? '',
    r.warenart ?? '',
    r.warenart_quelle ?? '',
    r.last_seen ? r.last_seen.split('-').reverse().join('.') : '',
    formatNumber(r.total_net),
  ]

  const lines: string[] = [
    headers.map(escape).join(SEP),
    ...rows.map(r => toRow(r).map(escape).join(SEP)),
  ]

  const csv = BOM + lines.join(CRLF) + CRLF
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  // Release the object URL after a short delay to allow the download to start
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * Point-in-time invoice-vs-catalog deviations for a single article
 * (each invoice date joined to the catalog row valid ON that date).
 *
 * Hits GET /api/preiskatalog/articles/:supplierId/:artikelNr/deviations
 */
export interface ArticleDeviationPoint {
  invoice_date: string
  invoice_price: number
  line_count: number
  qty_total: number | null
  catalog_price: number | null
  catalog_valid_from: string | null
  delta_eur: number | null
  delta_pct: number | null
}
export function useArticleDeviations(supplierId: string | null, artikelNr: string | null) {
  return useQuery({
    queryKey: ['catalog-article-deviations', supplierId, artikelNr] as const,
    queryFn: async (): Promise<{ count: number; points: ArticleDeviationPoint[] }> => {
      const res = await fetch(
        `/api/preiskatalog/articles/${encodeURIComponent(supplierId!)}/${encodeURIComponent(artikelNr!)}/deviations`,
        { credentials: 'include' },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Abweichungen konnten nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled: Boolean(supplierId) && Boolean(artikelNr),
    staleTime: 60_000,
  })
}

/**
 * Every valid invoice line of one article (drawer "Rechnungen mit diesem Artikel").
 * qty = 0 → "ohne Menge"; negative qty/net → Gutschrift.
 *
 * Hits GET /api/preiskatalog/articles/:supplierId/:artikelNr/rechnungen
 */
export interface ArticleRechnungRow {
  id: string
  /** ISO date YYYY-MM-DD */
  invoice_date: string | null
  invoice_nr: string | null
  school_id: string | null
  kostenstelle: string | null
  /** dim.School.schul_name when no Kostenstelle name exists */
  school_name: string | null
  qty: number | null
  unit: string | null
  net: number | null
  source: RechnungQuelle
}
export function useArticleRechnungen(supplierId: string | null, artikelNr: string | null) {
  return useQuery({
    queryKey: ['catalog-article-rechnungen', supplierId, artikelNr] as const,
    queryFn: async (): Promise<{ total: number; rows: ArticleRechnungRow[] }> => {
      const res = await fetch(
        `/api/preiskatalog/articles/${encodeURIComponent(supplierId!)}/${encodeURIComponent(artikelNr!)}/rechnungen`,
        { credentials: 'include' },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Rechnungen konnten nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled: Boolean(supplierId) && Boolean(artikelNr),
    staleTime: 60_000,
  })
}

// ─── Change log (mig 255) ─────────────────────────────────────────────────────

/** One row of ops.CatalogChangeLog from GET /api/preiskatalog/changes. */
export interface CatalogChange {
  id: number
  import_batch_id: string
  supplier_id: string
  artikel_nr: string
  artikelname: string | null
  change_type: 'new' | 'price' | 'attr' | 'removed'
  field: string
  old_value: string | null
  new_value: string | null
  /** ISO datetime (UTC) */
  changed_at: string
  source_type: 'file_upload' | 'api_pull' | 'api_push'
  catalog_date: string | null
  imported_by: string | null
}

/**
 * Catalog change history — every new article, price change and attribute change
 * per commit (upload wizard + ChefsCulinar push). Filters are optional; newest first.
 */
export function useCatalogChanges(p: { supplierId?: string | null; artikelNr?: string | null; batchId?: string | null; limit?: number }, enabled = true) {
  const qs = new URLSearchParams()
  if (p.supplierId && p.supplierId !== 'alle') qs.set('supplier_id', p.supplierId)
  if (p.artikelNr) qs.set('artikel_nr', p.artikelNr)
  if (p.batchId) qs.set('batch_id', p.batchId)
  if (p.limit) qs.set('limit', String(p.limit))
  return useQuery({
    queryKey: ['catalog-changes', p.supplierId ?? null, p.artikelNr ?? null, p.batchId ?? null, p.limit ?? null] as const,
    queryFn: async (): Promise<{ changes: CatalogChange[] }> => {
      const res = await fetch(`/api/preiskatalog/changes?${qs.toString()}`, { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Änderungsprotokoll konnte nicht geladen werden (${res.status})`)
      }
      return res.json()
    },
    enabled,
    staleTime: 60_000,
  })
}
